/**
 * CarouselView.jsx — Carousel Composer workspace: count and warnings, restore
 * bar, composer, slide strip, slide cards and the slide-list box.
 *
 * Cell interaction is delegated from the slides host. A pan drag writes the
 * dragged cell's style directly (panorama drags repaint every part of the group
 * once per frame) and commits to the store on pointerup, so the slide list does
 * not re-render per pointermove. Wheel zoom uses a non-passive native listener.
 *
 * The strip is also where slides are reordered. That drag is delegated the same
 * way and, like the cell drag, a finger has to earn it with a press-and-hold:
 * a plain swipe still scrolls the strip. Nothing moves until the drop — only a
 * line shows where the slide would land — and the line sits on block edges, so a
 * panorama run is never split.
 *
 * THE PRESS-AND-HOLD NEEDS BOTH HALVES. A hold that only has to outlast a timer
 * loses to an ordinary finger: a sideways scroll that eases in had not cleared
 * the old 8px inside 320ms, so it was claimed as a drag and silently reordered
 * the carousel. A drag now needs the timer AND a finger that stayed inside
 * HOLD_SLOP for the whole of it, and the budget outlives the timer — the first
 * move past it ends any chance of a drag, however late it lands.
 *
 * The three viewing modes only change how wide the list is and how much of a
 * slide's panel comes with it; every mode renders the same SlideCard.
 */

import { memo, useEffect, useRef } from 'react';
import {
  clearSlides, commitPan, dismissSaved, dropPhotoAt, getState, openPicker, resetCropAt,
  restoreSaved, selectImgById, selectSaved, setViewMode, useCarousel, wheelAt,
} from '../../hooks/useCarouselStore';
import { useMobile } from '../../hooks/useMobile';
import { beginPan, formatSize, panTo } from '../../lib/carousel/geometry.js';
import { readUI } from '../../lib/carousel/persist.js';
import ComposerBar from './ComposerBar';
import RecipeBox from './RecipeBox';
import SlideCard, {
  commitPendingDelete, moveUnitBeforeId, moveUnitBy, undoDelete, usePendingDelete,
} from './SlideCard';
import SlideStage, { paintPanoGroup, useSlidePreviews } from './SlideStage';
import { afterRender, scrollToSlide } from './dom.js';
import './review.css';

const MODES = [
  ['focus', 'Focus', 'One slide per row, with every setting'],
  ['compare', 'Compare', 'Two across, with a compact bar'],
  ['grid', 'Grid', 'A contact sheet for reordering'],
];
const MODE_KEYS = MODES.map(([k]) => k);
// Compare needs room for two full stages; below that a phone gets today's list.
const WIDE = 1400;
// A finger earns a drag by holding still. The same 450ms the library tile's
// long-press already uses, so the app has one press-and-hold, and a travel budget
// tight enough that a scroll easing in has spent it long before the timer lands.
const HOLD_MS = 450;
const HOLD_SLOP = 4;
let modePicked = false;

const StripItem = memo(function StripItem({ sl, si, pos, unit, head, first, last, design, group, imgById, previews }) {
  return (
    <div
      className={'strip-item' + (sl.pano ? ' pano' : '')}
      data-sid={sl.id}
      data-unit={unit}
      data-head={head}
    >
      <button type="button" className="cc-pick" title={'Slide ' + pos} onClick={() => scrollToSlide(si)}>
        <SlideStage sl={sl} si={si} design={design} group={group} imgById={imgById} interactive={false} graded previews={previews} />
        <span className="strip-n">{pos}</span>
      </button>
      <span className="cc-mv">
        <button
          type="button"
          title="Move earlier (Alt+←)"
          aria-label={'Move slide ' + pos + ' earlier'}
          disabled={first}
          onClick={() => moveUnitBy(sl.id, -1)}
        >
          ‹
        </button>
        <button
          type="button"
          title="Move later (Alt+→)"
          aria-label={'Move slide ' + pos + ' later'}
          disabled={last}
          onClick={() => moveUnitBy(sl.id, 1)}
        >
          ›
        </button>
      </span>
    </div>
  );
});

const cellAt = (e) => (e.target.closest ? e.target.closest('.cell[data-si]') : null);

// The list as it is shown: the slide waiting to be deleted is left out, so the
// numbering closes up, while si stays the real index every action is keyed by.
function reviewRows(slides, hidden) {
  const rows = [];
  let unit = -1;
  let head = '';
  let prevGid = null;
  let hid = false;
  slides.forEach((sl, si) => {
    if (sl.id === hidden) { hid = true; return; }
    const gid = sl.pano ? sl.pano.gid : null;
    if (!gid || gid !== prevGid) { unit += 1; head = sl.id; }
    prevGid = gid;
    rows.push({ sl, si, pos: rows.length + 1, unit, head });
  });
  return { rows, units: unit + 1, hid };
}

export default function CarouselView() {
  const { isMobile } = useMobile();
  const slides = useCarousel((s) => s.slides);
  const panos = useCarousel((s) => s.panos);
  const design = useCarousel((s) => s.design);
  const libN = useCarousel((s) => s.library.length);
  const saveNote = useCarousel((s) => s.saveNote);
  const viewMode = useCarousel((s) => s.viewMode);
  const exportOpen = useCarousel((s) => s.exportOpen);
  const imgById = useCarousel(selectImgById);
  const saved = useCarousel(selectSaved);
  const draftPrompt = useCarousel((s) => s.draftPrompt);
  const doomed = usePendingDelete();
  // the strip thumbnails are real stages, so they get the real grade too
  const pv = useSlidePreviews(slides, panos, imgById);
  const hostRef = useRef(null);
  const stripRef = useRef(null);
  const lineRef = useRef(null);
  const dragRef = useRef(null);
  const stripDragRef = useRef(null);

  // Whichever mode was chosen last, or the one the screen has room for.
  useEffect(() => {
    if (modePicked) return;
    modePicked = true;
    const want = readUI().viewMode;
    setViewMode(MODE_KEYS.indexOf(want) >= 0 ? want : (window.innerWidth >= WIDE ? 'compare' : 'focus'));
  }, []);

  // Grid takes three or four across by how much room the list actually has (the
  // sidebar and the page padding are already out of it); compare drops to one on
  // a phone.
  useEffect(() => {
    const host = hostRef.current;
    if (!window.ResizeObserver) return undefined;
    const ro = new ResizeObserver(() => {
      const w = host.clientWidth;
      host.style.setProperty('--cc-grid-cols', w >= 1500 ? '4' : w >= 1000 ? '3' : '2');
      host.style.setProperty('--cc-cmp-cols', w >= 760 ? '2' : '1');
    });
    ro.observe(host);
    return () => ro.disconnect();
  }, []);

  // The slide format's own width/height, so compare can stop a portrait stage at
  // a screenful without the stylesheet knowing the format.
  useEffect(() => {
    const [W, H] = formatSize(design.fmt);
    hostRef.current.style.setProperty('--cc-ar', String(W / H));
  }, [design.fmt]);

  // The export sheet lists the real slides, so a delete waiting to be undone has
  // to land before it opens.
  useEffect(() => {
    if (exportOpen) commitPendingDelete();
  }, [exportOpen]);

  useEffect(() => {
    const host = hostRef.current;
    const onWheel = (e) => {
      const el = cellAt(e);
      if (!el || el.classList.contains('empty') || el.dataset.gid) return;   // a panorama's zoom is fixed by the seam
      e.preventDefault();
      wheelAt(+el.dataset.si, +el.dataset.ci, e.deltaY);
    };
    host.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      host.removeEventListener('wheel', onWheel);
      if (dragRef.current) dragRef.current();
      if (stripDragRef.current) stripDragRef.current();
      // leaving the page is an answer too: the slide the undo was offered for goes
      commitPendingDelete();
    };
  }, []);

  /**
   * Reframing a photo and scrolling the page are the same gesture on a finger, so
   * a touch has to earn the drag: hold still for a moment and the cell is grabbed
   * (and takes the gesture), move first and the browser scrolls as it should. A
   * mouse keeps dragging immediately.
   */
  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    const el = cellAt(e);
    if (!el || el.classList.contains('empty')) return;
    const si = +el.dataset.si, ci = +el.dataset.ci;
    const s = getState();
    const sl = s.slides[si];
    if (!sl) return;
    const lookup = selectImgById(s);
    const pan = beginPan(sl, ci, { panos: s.panos, imgById: lookup, design: s.design }, el.getBoundingClientRect().width);
    if (!pan) return;

    const host = hostRef.current;
    const touch = e.pointerType === 'touch';
    const x0 = e.clientX, y0 = e.clientY;
    let pos = { x: pan.x0, y: pan.y0 };
    let raf = 0;
    let live = false;
    let hold = 0;
    let still = true;           // has the finger stayed inside HOLD_SLOP so far?

    const paintPano = () => {
      raf = 0;
      const g = s.panos[pan.gid];
      if (g) paintPanoGroup(host, pan.gid, { ...g, start: pos.x, oy: pos.y }, getState().slides, lookup, s.design);
    };
    const claim = () => {
      hold = 0;
      if (!still) return;       // the finger was already travelling: it meant to scroll
      live = true;
      try { el.setPointerCapture(e.pointerId); } catch { /* pointer already released */ }
      el.classList.add('dragging', 'grabbed');
      if (touch && navigator.vibrate) navigator.vibrate(8);
    };
    const mv = (ev) => {
      const dx = ev.clientX - x0, dy = ev.clientY - y0;
      if (!live) {
        // spent the budget: the finger meant to scroll, whether or not the timer
        // has already fired
        if (Math.abs(dx) + Math.abs(dy) > HOLD_SLOP) { still = false; clearTimeout(hold); detach(); }
        return;
      }
      if (ev.cancelable) ev.preventDefault();
      pos = panTo(pan, dx, dy);
      if (pan.gid) { if (!raf) raf = requestAnimationFrame(paintPano); }
      else {
        const layer = el.querySelector('.cellimg') || el;
        layer.style.backgroundPosition = (pos.x * 100) + '% ' + (pos.y * 100) + '%';
      }
    };
    const detach = () => {
      clearTimeout(hold);
      cancelAnimationFrame(raf);
      el.classList.remove('dragging', 'grabbed');
      el.removeEventListener('pointermove', mv);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      dragRef.current = null;
    };
    function up() {
      const moved = live && (pos.x !== pan.x0 || pos.y !== pan.y0);
      detach();
      if (pan.gid && live) paintPano();
      if (moved) commitPan(si, pan, pos);
    }

    el.addEventListener('pointermove', mv);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    dragRef.current = detach;
    if (touch) hold = setTimeout(claim, HOLD_MS);
    else claim();
  };

  const onDoubleClick = (e) => {
    const el = cellAt(e);
    if (el && !el.classList.contains('empty')) resetCropAt(+el.dataset.si, +el.dataset.ci);
  };

  const onClick = (e) => {
    const el = cellAt(e);
    if (el && el.classList.contains('empty')) openPicker(+el.dataset.si, +el.dataset.ci);
  };

  const onDragOver = (e) => {
    const el = cellAt(e);
    if (!el) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    el.classList.add('target');
  };

  const onDragLeave = (e) => {
    const el = cellAt(e);
    if (el) el.classList.remove('target');
  };

  const onDrop = (e) => {
    const el = cellAt(e);
    if (!el) return;
    e.preventDefault();
    el.classList.remove('target');
    const id = e.dataTransfer.getData('text/x-img');
    if (id) dropPhotoAt(+el.dataset.si, +el.dataset.ci, id);
  };

  /**
   * Dragging a thumbnail to a new place. Same bargain as the cell drag: a finger
   * only gets the drag after a press-and-hold, and a swipe scrolls instead.
   *
   * A thumbnail allows the browser only pan-y (like a cell), because a sideways
   * drag and the strip's own sideways scrolling are the same gesture and the
   * browser would win it every time. So the sideways swipe is ours to do: we
   * move scrollLeft with the finger. A mouse waits for a few pixels, so a plain
   * click still jumps to the slide.
   */
  const onStripPointerDown = (e) => {
    if (e.button !== 0) return;
    const el = e.target.closest ? e.target.closest('.strip-item') : null;
    if (!el || e.target.closest('.cc-mv')) return;
    const strip = stripRef.current;
    const line = lineRef.current;
    const id = el.dataset.sid;
    const touch = e.pointerType === 'touch';
    const x0 = e.clientX, y0 = e.clientY;
    let live = false, moved = false, hold = 0, raf = 0, px = x0;
    let swiping = false, sx = 0, still = true;
    let before;

    // Block edges only: every part of a panorama shares one drop slot.
    const frame = () => {
      raf = 0;
      const box = strip.getBoundingClientRect();
      // a narrow edge band, so dragging onto the last thumbnail of a phone-width
      // strip is not read as "scroll to the end"
      const near = px < box.left + 30 ? -1 : px > box.right - 30 ? 1 : 0;
      if (near) strip.scrollLeft += near * 10;
      const units = [];
      strip.querySelectorAll('.strip-item').forEach((it) => {
        const u = +it.dataset.unit;
        const r = it.getBoundingClientRect();
        const cur = units[units.length - 1];
        if (cur && cur.u === u) cur.right = r.right;
        else units.push({ u, head: it.dataset.head, left: r.left, right: r.right });
      });
      const k = units.findIndex((u) => px < (u.left + u.right) / 2);
      const at = k < 0 ? null : units[k];
      before = at ? at.head : null;
      const edge = at ? at.left - 5 : units.length ? units[units.length - 1].right + 3 : box.left;
      line.style.left = (edge - box.left + strip.scrollLeft) + 'px';
      if (near) raf = requestAnimationFrame(frame);
    };
    const tick = () => { if (!raf) raf = requestAnimationFrame(frame); };
    const claim = () => {
      hold = 0;
      if (!still) return;       // the finger was already travelling: this is a swipe
      live = true;
      try { el.setPointerCapture(e.pointerId); } catch { /* pointer already released */ }
      el.classList.add('grabbed');
      strip.classList.add('cc-dragging');
      if (touch && navigator.vibrate) navigator.vibrate(8);
      tick();
    };
    const mv = (ev) => {
      px = ev.clientX;
      const dx = ev.clientX - x0, dy = ev.clientY - y0;
      if (!live) {
        if (touch) {
          if (!swiping) {
            if (Math.abs(dx) + Math.abs(dy) <= HOLD_SLOP) return;
            // budget spent: up and down is the page's, sideways is the strip's,
            // and the strip's is ours to move. The hold loses either way — there
            // is no arbiter on the strip, so this race is the only thing standing
            // between a scroll and a reorder that cannot be undone.
            still = false;
            clearTimeout(hold);
            hold = 0;
            if (Math.abs(dx) <= Math.abs(dy)) { detach(); return; }
            swiping = true;
            sx = strip.scrollLeft;
          }
          strip.scrollLeft = sx - dx;
          if (ev.cancelable) ev.preventDefault();
          return;
        }
        if (Math.abs(dx) + Math.abs(dy) < 5) return;
        claim();
      }
      if (ev.cancelable) ev.preventDefault();
      moved = true;
      tick();
    };
    const detach = () => {
      clearTimeout(hold);
      cancelAnimationFrame(raf);
      raf = 0;
      el.classList.remove('grabbed');
      strip.classList.remove('cc-dragging');
      el.removeEventListener('pointermove', mv);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', cancel);
      stripDragRef.current = null;
    };
    // the pointerup that ended a drag or a swipe must not also count as a click
    const killClick = () => {
      const kill = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
      strip.addEventListener('click', kill, { capture: true, once: true });
      setTimeout(() => strip.removeEventListener('click', kill, true), 0);
    };
    function cancel() {
      moved = false;
      detach();
    }
    function up() {
      const drop = live && moved;
      // settle on the last place the finger was, in case the frame never ran
      if (drop) { cancelAnimationFrame(raf); raf = 0; frame(); }
      const swiped = swiping;
      detach();
      if (drop || swiped) killClick();
      if (drop) moveUnitBeforeId(id, before);
    }

    el.addEventListener('pointermove', mv);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', cancel);
    stripDragRef.current = detach;
    if (touch) hold = setTimeout(claim, HOLD_MS);
  };

  // Alt+Arrow on a focused thumbnail: the same one-step move as its arrows.
  const onStripKeyDown = (e) => {
    if (!e.altKey || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
    const el = e.target.closest ? e.target.closest('.strip-item') : null;
    if (!el) return;
    e.preventDefault();
    const id = el.dataset.sid;
    moveUnitBy(id, e.key === 'ArrowLeft' ? -1 : 1);
    // the thumbnail moves out from under the keyboard, so follow it
    afterRender(() => {
      const next = document.querySelector('.cc-page .strip-item[data-sid="' + id + '"] .cc-pick');
      if (!next) return;
      next.focus();
      next.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  };

  const hidden = doomed ? doomed.id : null;
  const { rows, units, hid } = reviewRows(slides, hidden);
  const n = rows.length;
  const warn = n > 20 ? '— Instagram allows 20 per carousel' : n === 1 ? '— a carousel needs at least 2' : '';
  let restoreText = '';
  if (saved && !n) {
    const when = new Date(saved.ts);
    const stamp = Number.isNaN(when.getTime()) ? '' : ' from ' + when.toLocaleString();
    restoreText = 'A saved carousel of ' + saved.slides.length + ' slides' + stamp
      + (libN ? ' is waiting.' : ' is waiting — drop the same photos to bring it back.');
  }

  return (
    <section className="work">
      <div className="worktop">
        <div className="count"><b>{n}</b> slides <span className="flag">{warn}</span></div>
        {n > 0 && (
          <div className="seg cc-modes">
            {MODES.map(([k, label, title]) => (
              <button
                key={k}
                type="button"
                aria-pressed={viewMode === k}
                aria-label={label + ' view'}
                title={title}
                onClick={() => setViewMode(k)}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        <div className="spacer" />
        <span className="lbl">{saveNote && n ? 'autosaved in this browser' : ''}</span>
        <button type="button" className="btn tiny ghost" onClick={clearSlides}>Remove all slides</button>
      </div>

      {saved && !n && !draftPrompt && (
        <div className="restore">
          <span>{restoreText}</span>
          <span className="spacer" />
          {libN > 0 && <button type="button" className="btn tiny" onClick={restoreSaved}>Restore it</button>}
          <button type="button" className="btn tiny ghost" onClick={dismissSaved}>Dismiss</button>
        </div>
      )}

      {/* Pick owns the tray and the add-a-slide controls; on a phone Review has
          no room for a second copy of them above its first slide */}
      {!isMobile && <ComposerBar />}

      <div
        className={'strip cc-strip' + (n > 1 ? ' on' : '')}
        ref={stripRef}
        onPointerDown={onStripPointerDown}
        onKeyDown={onStripKeyDown}
      >
        <span className="lbl strip-label">Slides</span>
        {rows.map((r) => (
          <StripItem
            key={r.sl.id}
            sl={r.sl}
            si={r.si}
            pos={r.pos}
            unit={r.unit}
            head={r.head}
            first={r.unit === 0}
            last={r.unit === units - 1}
            design={design}
            group={r.sl.pano ? panos[r.sl.pano.gid] : null}
            imgById={imgById}
            previews={pv}
          />
        ))}
        <span className="cc-dropline" ref={lineRef} aria-hidden="true" />
      </div>

      <div
        className={'slides cc-mode-' + viewMode}
        ref={hostRef}
        onPointerDown={onPointerDown}
        onDoubleClick={onDoubleClick}
        onClick={onClick}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {rows.map((r) => (
          <SlideCard
            key={r.sl.id}
            sl={r.sl}
            si={r.si}
            pos={r.pos}
            mode={viewMode}
            first={r.unit === 0}
            last={r.unit === units - 1}
            design={design}
            group={r.sl.pano ? panos[r.sl.pano.gid] : null}
            imgById={imgById}
          />
        ))}
      </div>

      {doomed && hid && (
        <div className="cc-undo" role="status">
          <span>{'Slide ' + doomed.pos + ' deleted'}</span>
          <button type="button" className="btn tiny" onClick={undoDelete}>Undo</button>
        </div>
      )}

      {!n && (
        <div className="empty-state">
          <h2>No slides yet</h2>
          Open the Library, select a few frames, then add them as singles, pairs, a hero grid or a panorama.<br />
          Or paste a slide list into the box below and build the whole carousel at once.
        </div>
      )}

      <RecipeBox />
    </section>
  );
}
